/**
 * Manage Team Offers - View and manage offers for a specific team
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { UserPlus, Mail, RefreshCw, Trash2, CheckCircle, XCircle, Clock } from 'lucide-react';
import apiService from '../../services/api';
import { AdminPageLayout, ConfirmDialog, Button, OfferModal, Section, Card, Badge } from '../../components';
import {
  Listing,
  ListingFilter,
  ListingItem,
  ListingActionBar
} from '../../components/listing/v2';
import { CreateAction } from '../../components/listing/v2/ListingAction';
import { generateNestedBreadcrumbs } from '../../utils/breadcrumbGenerator';
import { useToast } from '../../contexts/ToastContext';

const ManageTeamOffers = () => {
  const { teamId, branch_id } = useParams();
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [offers, setOffers] = useState([]);
  const [totalOffers, setTotalOffers] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    loadData();
  }, [teamId, filters.status]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [teamData, offersData] = await Promise.all([
        apiService.getTeam(teamId),
        apiService.listTeamOffers(teamId, {
          status: filters.status,
          limit: 100,
          offset: 0
        })
      ]);

      setTeam(teamData);
      setOffers(offersData.offers || []);
      setTotalOffers(offersData.total || 0);
    } catch (err) {
      console.error('Failed to load offers:', err);
      showError(err.message || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffer = async (offerData) => {
    try {
      setOfferLoading(true);
      await apiService.createOffer(teamId, offerData);

      showSuccess('Offer created successfully');
      setShowOfferModal(false);
      await loadData();
    } catch (err) {
      showError(err.message || 'Failed to create offer');
      throw err; // Re-throw to prevent modal from closing
    } finally {
      setOfferLoading(false);
    }
  };

  const handleResendEmail = async (offer, emailType) => {
    try {
      setLoading(true);

      if (emailType === 'registration') {
        await apiService.resendOfferRegistrationEmail(offer.id);
      } else {
        await apiService.resendOfferAcceptanceEmail(offer.id);
      }

      showSuccess(`Email resent to ${offer.parent_email}`);
      await loadData();
    } catch (err) {
      showError(err.message || 'Failed to resend email');
    } finally {
      setLoading(false);
    }
  };

  const handleRenewOffer = async (offer) => {
    try {
      setLoading(true);

      await apiService.renewOffer(offer.id);
      showSuccess(`Offer renewed for ${offer.player_name}`);
      await loadData();
    } catch (err) {
      showError(err.message || 'Failed to renew offer');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOffer = async (offer) => {
    setConfirmAction({
      title: 'Cancel Offer',
      message: `Are you sure you want to cancel the offer for ${offer.player_name}?`,
      details: `Parent: ${offer.parent_name} (${offer.parent_email})`,
      onConfirm: async () => {
        try {
          setLoading(true);
          await apiService.cancelOffer(offer.id);
          showSuccess('Offer cancelled successfully');
          setShowConfirmDialog(false);
          setConfirmAction(null);
          await loadData();
        } catch (err) {
          showError(err.message || 'Failed to cancel offer');
        } finally {
          setLoading(false);
        }
      }
    });
    setShowConfirmDialog(true);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: ''
    });
  };

  const filteredOffers = offers.filter(offer => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!offer.player_name?.toLowerCase().includes(searchTerm) &&
        !offer.parent_name?.toLowerCase().includes(searchTerm) &&
        !offer.parent_email?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    return true;
  });

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'accepted':
        return 'success';
      case 'pending':
      case 'registered':
      case 'pending_acceptance':
        return 'info';
      case 'declined':
      case 'expired':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Awaiting Registration';
      case 'registered':
      case 'pending_acceptance':
        return 'Awaiting Acceptance';
      case 'accepted':
        return 'Accepted';
      case 'declined':
        return 'Declined';
      case 'expired':
        return 'Expired';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOfferExpired = (offer) => {
    return new Date(offer.expires_at) < new Date();
  };

  // Generate breadcrumbs
  const breadcrumbItems = team ? generateNestedBreadcrumbs(
    window.location.pathname,
    branch_id ? [
      { label: 'Branch Dashboard', path: `/branch/${branch_id}/dashboard` },
      { label: 'Teams', path: `/branch/${branch_id}/teams` },
      { label: team.name, path: `/branch/${branch_id}/teams` },
      { label: 'Offers', path: null }
    ] : [
      { label: 'Teams', path: '/admin/teams' },
      { label: team.name, path: '/admin/teams' },
      { label: 'Offers', path: null }
    ]
  ) : null;

  if (loading && !team) {
    return (
      <AdminPageLayout
        title="TEAM OFFERS"
        subtitle="Loading..."
        backPath={branch_id ? `/branch/${branch_id}/teams` : "/admin/teams"}
        maxWidth="1400px"
      >
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          Loading offers...
        </div>
      </AdminPageLayout>
    );
  }

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction
          onClick={() => setShowOfferModal(true)}
          label="Create Offer"
          icon={<UserPlus size={16} />}
        />
      }
    />
  );

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search by player name, parent name, or email..."
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: '', label: 'All Statuses' },
            { value: 'pending', label: 'Awaiting Registration' },
            { value: 'registered', label: 'Awaiting Acceptance' },
            { value: 'accepted', label: 'Accepted' },
            { value: 'declined', label: 'Declined' },
            { value: 'expired', label: 'Expired' }
          ]
        }
      ]}
      activeFilters={{ status: filters.status }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={handleClearFilters}
    />
  );

  const renderOfferActions = (offer) => {
    const actions = [];

    // Resend Email - only for pending or registered status
    if (offer.status === 'pending' || offer.status === 'registered' || offer.status === 'pending_acceptance') {
      actions.push(
        <Button
          key="resend"
          onClick={() => handleResendEmail(offer, offer.status === 'pending' ? 'registration' : 'acceptance')}
          variant="secondary"
          size="sm"
          disabled={loading}
        >
          <Mail size={14} style={{ marginRight: '6px' }} />
          Resend Email
        </Button>
      );
    }

    // Renew - only for expired or soon-to-expire offers
    if ((offer.status === 'expired' || isOfferExpired(offer)) && offer.status !== 'accepted') {
      actions.push(
        <Button
          key="renew"
          onClick={() => handleRenewOffer(offer)}
          variant="primary"
          size="sm"
          disabled={loading}
        >
          <RefreshCw size={14} style={{ marginRight: '6px' }} />
          Renew
        </Button>
      );
    }

    // Cancel - only for non-accepted/non-declined offers
    if (offer.status !== 'accepted' && offer.status !== 'declined') {
      actions.push(
        <Button
          key="cancel"
          onClick={() => handleCancelOffer(offer)}
          variant="danger"
          size="sm"
          disabled={loading}
        >
          <Trash2 size={14} style={{ marginRight: '6px' }} />
          Cancel
        </Button>
      );
    }

    return <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{actions}</div>;
  };

  return (
    <AdminPageLayout
      title="TEAM OFFERS"
      subtitle="Manage player invitations for this team"
      backPath={branch_id ? `/branch/${branch_id}/teams` : "/admin/teams"}
      breadcrumbs={breadcrumbItems}
      maxWidth="1400px"
    >
      {/* Team Context */}
      <Section title="TEAM INFORMATION">
        <Card level={1}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--color-text)', margin: '0 0 8px 0' }}>
                {team?.name}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                {team?.age_group_label && (
                  <span style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                    {team.age_group_label}
                  </span>
                )}
                {team?.season && (
                  <span style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                    {team.season}
                  </span>
                )}
                {team?.status && (
                  <Badge variant={team.status === 'active' ? 'success' : 'warning'} size="sm">
                    {team.status}
                  </Badge>
                )}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', marginBottom: '4px' }}>
                Enrollment:
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--color-text)' }}>
                {team?.current_enrollment || 0} / {team?.max_capacity || 0}
              </div>
            </div>
          </div>
        </Card>
      </Section>

      <Section
        title="OFFERS"
        subtitle={`${filteredOffers.length} ${filteredOffers.length === 1 ? 'offer' : 'offers'}`}
      >
        <Listing
          loading={loading}
          items={filteredOffers}
          actions={headerActions}
          filters={filterBar}
          variant="list"
          renderItem={(offer) => (
            <ListingItem
              key={offer.id}
              title={offer.player_name}
              subtitle={`${offer.parent_name} (${offer.parent_email})`}
              actions={renderOfferActions(offer)}
            >
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <Badge variant={getStatusVariant(offer.status)} size="sm">
                  {getStatusLabel(offer.status)}
                </Badge>
                {isOfferExpired(offer) && offer.status !== 'accepted' && (
                  <Badge variant="danger" size="sm">
                    <Clock size={12} style={{ marginRight: '4px' }} />
                    EXPIRED
                  </Badge>
                )}
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                  Created: {formatDate(offer.created_at)}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                  Expires: {formatDate(offer.expires_at)}
                  {isOfferExpired(offer) && offer.status !== 'accepted' && (
                    <span style={{ color: 'var(--color-error)', fontWeight: '600' }}> (Expired)</span>
                  )}
                </div>
                {offer.accepted_at && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                    Accepted: {formatDate(offer.accepted_at)}
                  </div>
                )}
                {offer.declined_at && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                    Declined: {formatDate(offer.declined_at)}
                  </div>
                )}
                {offer.renewed_count > 0 && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                    Renewals: {offer.renewed_count}
                  </div>
                )}
              </div>
            </ListingItem>
          )}
          emptyMessage="No offers found"
        />
      </Section>

      <OfferModal
        isOpen={showOfferModal}
        onClose={() => setShowOfferModal(false)}
        onSubmit={handleCreateOffer}
        team={team}
        loading={offerLoading}
      />

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setConfirmAction(null);
        }}
        onConfirm={confirmAction?.onConfirm}
        title={confirmAction?.title}
        message={confirmAction?.message}
        details={confirmAction?.details}
        variant="danger"
        confirmText="Confirm"
        cancelText="Cancel"
        loading={loading}
      />
    </AdminPageLayout>
  );
};

export default ManageTeamOffers;
